import { useState } from 'react';
import { Card, Row, Col, Typography, Button, Space, Avatar, Badge, Divider, Tag, Input, Switch, Drawer, Flex, Result, Spin, theme } from 'antd';
import { VideoCameraOutlined, UserOutlined, PhoneOutlined, MailOutlined, SettingOutlined, MessageOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { MdMic, MdVideocam } from 'react-icons/md';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { StructuredNoteContent } from '@/components/structured-note';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getStatusTagColor, getAppointmentStatusLabel } from '@/components/appointments/calendar/calendarUtils';
import { VideoEmbed } from '@/components/video-meeting/VideoEmbed';
import { FileUpload } from '@/components/video-meeting/FileUpload';
import { MeetingChat } from '@/components/video-meeting/MeetingChat';

dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

function VideoMeetingComponent() {
  const { meetingId } = useParams({ from: '/video-meeting/$meetingId' });
  const { t } = useTranslation('auth');
  const { token } = theme.useToken();
  const [isMicrophoneEnabled, setIsMicrophoneEnabled] = useState(true);
  const [isCameraEnabled, setIsCameraEnabled] = useState(true);
  const [files, setFiles] = useState<any[]>([]);
  const [chatMessages, setChatMessages] = useState<any[]>([
    {
      id: '1',
      sender: 'agent',
      message: 'Welcome to the video meeting! Please ensure your documents are ready.',
      timestamp: new Date().toISOString()
    }
  ]);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [meetingNotes, setMeetingNotes] = useState('');

  // TODO: connect to real appointment data source (useAppointmentQuery was removed with @/api/appointments)
  const appointment = null as any;
  const isLoading = false;

  const handleFileUpload = (file: any) => {
    setFiles(prev => [...prev, file]);
  };

  const handleFileDelete = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleSendMessage = (message: string) => {
    const newMessage = {
      id: Date.now().toString(),
      sender: 'user', // In real app, this would be determined by auth
      message,
      timestamp: new Date().toISOString()
    };
    setChatMessages(prev => [...prev, newMessage]);
  };

  if (isLoading) {
    return (
      <Flex
        vertical
        justify="center"
        align="center"
        gap={token.marginMD}
        style={{ minHeight: '100vh', padding: token.padding, background: token.colorBgLayout }}
      >
        <Spin size="large" />
        <Text type="secondary">{t('auth:videoMeeting.loading')}</Text>
      </Flex>
    );
  }

  if (!appointment) {
    return (
      <Flex
        justify="center"
        align="center"
        style={{ minHeight: '100vh', padding: token.padding, background: token.colorBgLayout }}
      >
        <Card
          style={{ width: 'min(480px, 100%)' }}
        >
          <Result
            status="warning"
            icon={<VideoCameraOutlined style={{ color: token.colorTextTertiary }} />}
            title={t('auth:videoMeeting.notFoundTitle')}
            subTitle={t('auth:videoMeeting.notFoundDescription')}
            style={{ padding: token.paddingLG }}
          />
        </Card>
      </Flex>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <div style={{ 
        backgroundColor: 'white', 
        padding: '20px 32px', 
        borderBottom: '1px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div>
          <Title level={2} style={{ margin: 0, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
            <VideoCameraOutlined style={{ color: '#1890ff' }} />
            {appointment.title}
          </Title>
          <Text type="secondary" style={{ fontSize: 16 }}>
            {dayjs(appointment.startTime).format('MMM DD, YYYY • HH:mm')} - 
            {dayjs(appointment.endTime).format('HH:mm')} ({appointment.duration} min)
          </Text>
        </div>
        <Space size="middle">
          <Badge 
            color={getStatusTagColor(appointment.status.toUpperCase())}
            text={getAppointmentStatusLabel(appointment.status.toUpperCase(), t)}
            style={{ fontSize: 14, fontWeight: 500 }}
          />
          <Button 
            icon={<SettingOutlined />} 
            onClick={() => setSettingsVisible(true)}
            size="large"
          >
            Settings
          </Button>
        </Space>
      </div>

      <div style={{ padding: '32px' }}>
        <Row gutter={[32, 32]}>
          {/* Main Video Area */}
          <Col xs={24} lg={16}>
            <Card 
              style={{ 
                marginBottom: 32,
                borderRadius: 12,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
              }}
              styles={{ body: { padding: 0 } }}
            >
              <VideoEmbed 
                meetingUrl={appointment.meetingUrl || ''} 
                meetingId={appointment.meetingId || ''} 
              />
            </Card>

            {/* Meeting Info */}
            <Card 
              title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  <span style={{ fontSize: 18, fontWeight: 600 }}>Meeting Information</span>
                </div>
              }
              style={{ 
                borderRadius: 12,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
              }}
              styles={{ body: { padding: '24px' } }}
            >
              <Row gutter={[24, 24]}>
                <Col xs={24} sm={12}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 16, 
                    padding: '16px',
                    backgroundColor: '#fafafa',
                    borderRadius: 8,
                    border: '1px solid #f0f0f0'
                  }}>
                    <Avatar size={56} icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>{appointment.clientName}</div>
                      {appointment.clientEmail && (
                        <div style={{ fontSize: 14, color: '#666', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <MailOutlined />
                          {appointment.clientEmail}
                        </div>
                      )}
                      {appointment.clientPhone && (
                        <div style={{ fontSize: 14, color: '#666', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <PhoneOutlined />
                          {appointment.clientPhone}
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <div style={{ 
                    padding: '16px',
                    backgroundColor: '#fafafa',
                    borderRadius: 8,
                    border: '1px solid #f0f0f0'
                  }}>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong style={{ fontSize: 14, color: '#666' }}>Agent:</Text>
                      <div style={{ fontSize: 16, fontWeight: 500, marginTop: 4 }}>
                        {appointment.agentName || 'Not assigned'}
                      </div>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <Text strong style={{ fontSize: 14, color: '#666' }}>Meeting ID:</Text>
                      <div style={{ fontSize: 16, fontWeight: 500, marginTop: 4, fontFamily: 'monospace' }}>
                        {appointment.meetingId}
                      </div>
                    </div>
                    {appointment.meetingPassword && (
                      <div>
                        <Text strong style={{ fontSize: 14, color: '#666' }}>Password:</Text>
                        <div style={{ fontSize: 16, fontWeight: 500, marginTop: 4, fontFamily: 'monospace' }}>
                          {appointment.meetingPassword}
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
              
              {appointment.description && (
                <>
                  <Divider style={{ margin: '24px 0' }} />
                  <div>
                    <Text strong style={{ fontSize: 16, color: '#262626' }}>Description:</Text>
                    <Paragraph style={{ marginTop: 12, marginBottom: 0, fontSize: 15, lineHeight: 1.6 }}>
                      {appointment.description}
                    </Paragraph>
                  </div>
                </>
              )}

              {appointment.notes && (
                <>
                  <Divider style={{ margin: '24px 0' }} />
                  <div>
                    <Text strong style={{ fontSize: 16, color: '#262626' }}>Notes:</Text>
                    <StructuredNoteContent value={appointment.notes} />
                  </div>
                </>
              )}

              {appointment.tags.length > 0 && (
                <>
                  <Divider style={{ margin: '24px 0' }} />
                  <div>
                    <Text strong style={{ fontSize: 16, color: '#262626', marginBottom: 12, display: 'block' }}>Tags:</Text>
                    <Space wrap size={[8, 8]}>
                      {appointment.tags.map(tag => (
                        <Tag 
                          key={tag} 
                          style={{ 
                            padding: '4px 12px', 
                            borderRadius: 16, 
                            fontSize: 13,
                            fontWeight: 500
                          }}
                        >
                          {tag}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                </>
              )}
            </Card>
          </Col>

          {/* Sidebar */}
          <Col xs={24} lg={8}>
            <Space orientation="vertical" style={{ width: '100%' }} size="large">
              {/* File Upload */}
              <FileUpload
                files={files}
                onUpload={handleFileUpload}
                onDelete={handleFileDelete}
                userType="client" // In real app, this would be determined by auth
              />

              {/* Chat */}
              <MeetingChat
                messages={chatMessages}
                onSendMessage={handleSendMessage}
              />

              {/* Meeting Notes */}
              <Card 
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <MessageOutlined style={{ color: '#1890ff' }} />
                    <span style={{ fontSize: 16, fontWeight: 600 }}>Meeting Notes</span>
                  </div>
                }
                style={{ 
                  borderRadius: 12,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }}
                styles={{ body: { padding: '20px' } }}
              >
                <TextArea
                  value={meetingNotes}
                  onChange={(e) => setMeetingNotes(e.target.value)}
                  placeholder="Add notes during the meeting..."
                  rows={4}
                  style={{ marginBottom: 16 }}
                />
                <div style={{ textAlign: 'right' }}>
                  <Button type="primary" size="middle">
                    Save Notes
                  </Button>
                </div>
              </Card>
            </Space>
          </Col>
        </Row>
      </div>

      {/* Settings Drawer */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SettingOutlined style={{ color: '#1890ff' }} />
            <span style={{ fontSize: 18, fontWeight: 600 }}>Meeting Settings</span>
          </div>
        }
        placement="right"
        onClose={() => setSettingsVisible(false)}
        open={settingsVisible}
        width={420}
        styles={{ body: { padding: '24px' } }}
      >
        <Space orientation="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Title level={4} style={{ marginBottom: 16, color: '#262626' }}>Audio & Video</Title>
            <Space orientation="vertical" style={{ width: '100%' }} size="middle">
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '12px 16px',
                backgroundColor: '#fafafa',
                borderRadius: 8,
                border: '1px solid #f0f0f0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <MdMic style={{ fontSize: 18, color: '#1890ff' }} />
                  <span style={{ fontWeight: 500 }}>Microphone</span>
                </div>
                <Switch 
                  checked={isMicrophoneEnabled}
                  onChange={setIsMicrophoneEnabled}
                  checkedChildren="On"
                  unCheckedChildren="Off"
                />
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '12px 16px',
                backgroundColor: '#fafafa',
                borderRadius: 8,
                border: '1px solid #f0f0f0'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <MdVideocam style={{ fontSize: 18, color: '#1890ff' }} />
                  <span style={{ fontWeight: 500 }}>Camera</span>
                </div>
                <Switch 
                  checked={isCameraEnabled}
                  onChange={setIsCameraEnabled}
                  checkedChildren="On"
                  unCheckedChildren="Off"
                />
              </div>
            </Space>
          </div>

          <div>
            <Title level={4} style={{ marginBottom: 16, color: '#262626' }}>Meeting Controls</Title>
            <Space orientation="vertical" style={{ width: '100%' }} size="middle">
              <Button block size="large" style={{ height: 44 }}>
                Start Recording
              </Button>
              <Button block size="large" style={{ height: 44 }}>
                Share Screen
              </Button>
              <Button block size="large" style={{ height: 44 }}>
                Invite Participants
              </Button>
            </Space>
          </div>

          <div>
            <Title level={4} style={{ marginBottom: 16, color: '#262626' }}>Meeting Information</Title>
            <div style={{ 
              padding: '16px',
              backgroundColor: '#fafafa',
              borderRadius: 8,
              border: '1px solid #f0f0f0'
            }}>
              <div style={{ marginBottom: 12 }}>
                <Text strong style={{ fontSize: 13, color: '#666' }}>Meeting ID:</Text>
                <div style={{ fontSize: 14, fontFamily: 'monospace', marginTop: 4 }}>
                  {appointment.meetingId}
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <Text strong style={{ fontSize: 13, color: '#666' }}>Password:</Text>
                <div style={{ fontSize: 14, fontFamily: 'monospace', marginTop: 4 }}>
                  {appointment.meetingPassword}
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <Text strong style={{ fontSize: 13, color: '#666' }}>Duration:</Text>
                <div style={{ fontSize: 14, marginTop: 4 }}>
                  {appointment.duration} minutes
                </div>
              </div>
              <div>
                <Text strong style={{ fontSize: 13, color: '#666' }}>Status:</Text>
                <div style={{ marginTop: 4 }}>
                  <Badge 
                    color={getStatusTagColor(appointment.status.toUpperCase())}
                    text={getAppointmentStatusLabel(appointment.status.toUpperCase(), t)}
                  />
                </div>
              </div>
            </div>
          </div>
        </Space>
      </Drawer>
    </div>
  );
}

export const Route = createFileRoute('/video-meeting/$meetingId')({
  component: VideoMeetingComponent
});